import React from 'react';
import { useTranslation } from 'react-i18next';
import HtmlContent from '../HtmlContent/HtmlContent';
import { splitQuotedHtml } from '../../utils/collapseQuotedHtml';

interface CollapsibleQuotedHtmlProps {
    html: string;
    previousHtmls?: string[];
    className?: string;
    style?: React.CSSProperties;
    projectId?: string;
}

/**
 * Render message HTML, collapsing quoted history (overlap with previous messages
 * or trailing/leading blockquotes) behind a Gmail-like toggle.
 */
const CollapsibleQuotedHtml = (props: CollapsibleQuotedHtmlProps) => {
    const { html, previousHtmls = [], className, style, projectId } = props;
    const { t } = useTranslation();
    const [expanded, setExpanded] = React.useState(false);
    const previousKey = previousHtmls.join('\0');

    const split = React.useMemo(
        () => splitQuotedHtml(html || '', previousHtmls),
        // previousHtmls is represented by previousKey for stable memoization
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [html, previousKey]
    );

    React.useEffect(() => {
        setExpanded(false);
    }, [html, previousKey]);

    if (!split) {
        return (
            <HtmlContent
                html={html}
                className={className}
                style={style}
                projectId={projectId}
            />
        );
    }

    return (
        <div className={className} style={style}>
            <HtmlContent html={split.visibleHtml} projectId={projectId} />
            <div className="yoda-quoted-toggle">
                <button
                    type="button"
                    className="yoda-quoted-toggle__button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((value) => !value)}
                >
                    {expanded
                        ? (t('changes.hide_quoted_text') || 'Hide quoted text')
                        : (t('changes.show_quoted_text') || 'Show quoted text')}
                </button>
            </div>
            {expanded && (
                <div className="yoda-quoted-history">
                    <HtmlContent html={split.quotedHtml} projectId={projectId} />
                </div>
            )}
        </div>
    );
};

export default CollapsibleQuotedHtml;
